import { useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Zap,
  Briefcase,
  Heart,
  Sparkles,
  Shield,
  Users,
  Check,
  Sun,
  Moon,
} from 'lucide-react';
import type { Step1Data, ThemeMode, Tone, Priority } from '../../../types/onboarding';
import { TONE_OPTIONS, PRIORITY_OPTIONS } from '../../../types/onboarding';

interface Step1Props {
  data: Step1Data | null;
  onChange: (data: Step1Data) => void;
  onNext: () => void;
  onBack: () => void;
  businessName: string;
  saving: boolean;
}

const TONE_ICONS: Record<string, typeof Zap> = {
  Zap,
  Briefcase,
  Heart,
  Sparkles,
  Shield,
  Users,
};

export function Step1ThemeAndTone({
  data,
  onChange,
  onNext,
  onBack,
  businessName,
  saving,
}: Step1Props) {
  const [themeMode, setThemeMode] = useState<ThemeMode>(
    data?.themeMode || 'light'
  );
  const [tone, setTone] = useState<Tone | null>(data?.tone || null);
  const [priorities, setPriorities] = useState<Priority[]>(
    data?.priorities || []
  );

  // Handle theme mode selection
  const handleThemeModeChange = (value: ThemeMode) => {
    setThemeMode(value);
    onChange({ themeMode: value, tone: tone || undefined, priorities });
  };

  // Handle tone selection
  const handleToneChange = (value: Tone) => {
    setTone(value);
    onChange({ themeMode, tone: value, priorities });
  };

  // Handle priority toggle
  const togglePriority = (value: Priority) => {
    let newPriorities: Priority[];
    if (priorities.includes(value)) {
      newPriorities = priorities.filter((p) => p !== value);
    } else if (priorities.length < 2) {
      newPriorities = [...priorities, value];
    } else {
      return; // Can't add more than 2
    }
    setPriorities(newPriorities);
    onChange({ themeMode, tone: tone || undefined, priorities: newPriorities });
  };

  const canProceed = tone !== null;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Theme & Tone
        </h1>
        <p className="text-gray-600">
          Choose your website's visual theme and voice for {businessName}.
        </p>
      </div>

      {/* Theme Mode Selection */}
      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">
          Choose your theme
        </h2>
        <div className="grid grid-cols-2 gap-4">
          {/* Light Theme */}
          <button
            onClick={() => handleThemeModeChange('light')}
            className={`
              relative p-6 rounded-xl border-2 text-center transition-all
              ${
                themeMode === 'light'
                  ? 'border-primary-600 bg-white ring-2 ring-primary-600 ring-offset-2'
                  : 'border-gray-200 bg-white hover:border-primary-300'
              }
            `}
          >
            {themeMode === 'light' && (
              <div className="absolute top-3 right-3 w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
            <Sun className={`w-10 h-10 mx-auto mb-3 ${themeMode === 'light' ? 'text-primary-600' : 'text-gray-400'}`} />
            <h3 className="font-medium text-gray-900">Light</h3>
            <p className="text-sm text-gray-500 mt-1">
              Clean & bright
            </p>
          </button>

          {/* Dark Theme */}
          <button
            onClick={() => handleThemeModeChange('dark')}
            className={`
              relative p-6 rounded-xl border-2 text-center transition-all
              ${
                themeMode === 'dark'
                  ? 'border-primary-600 bg-gray-900 ring-2 ring-primary-600 ring-offset-2'
                  : 'border-gray-200 bg-gray-900 hover:border-primary-300'
              }
            `}
          >
            {themeMode === 'dark' && (
              <div className="absolute top-3 right-3 w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
            )}
            <Moon className={`w-10 h-10 mx-auto mb-3 ${themeMode === 'dark' ? 'text-primary-400' : 'text-gray-500'}`} />
            <h3 className="font-medium text-white">Dark</h3>
            <p className="text-sm text-gray-400 mt-1">
              Bold & modern
            </p>
          </button>
        </div>
      </div>

      {/* Tone Selection */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            How should your website sound?
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            This shapes the voice of your content
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TONE_OPTIONS.map((option) => {
            const Icon = TONE_ICONS[option.icon];
            const isSelected = tone === option.value;

            return (
              <button
                key={option.value}
                onClick={() => handleToneChange(option.value)}
                className={`
                  relative p-4 rounded-xl border-2 text-left transition-all
                  ${
                    isSelected
                      ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-600 ring-offset-2'
                      : 'border-gray-200 hover:border-primary-300 hover:bg-gray-50'
                  }
                `}
              >
                {isSelected && (
                  <div className="absolute top-3 right-3 w-5 h-5 bg-primary-600 rounded-full flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <div
                    className={`
                    w-10 h-10 rounded-lg flex items-center justify-center shrink-0
                    ${isSelected ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600'}
                  `}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-gray-900">{option.label}</h3>
                    <p className="text-xs text-primary-600 font-medium mt-0.5">
                      "{option.tagline}"
                    </p>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {option.description}
                    </p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Priorities selection */}
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            What matters most for your website?
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Select up to 2 priorities (optional)
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {PRIORITY_OPTIONS.map((option) => {
            const isSelected = priorities.includes(option.value);
            const isDisabled = !isSelected && priorities.length >= 2;

            return (
              <button
                key={option.value}
                onClick={() => togglePriority(option.value)}
                disabled={isDisabled}
                className={`
                  px-4 py-2 rounded-full text-sm font-medium transition-all
                  ${
                    isSelected
                      ? 'bg-primary-600 text-white'
                      : isDisabled
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }
                `}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed || saving}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all
            ${
              canProceed
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Continue
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
